/**
 * Single source of truth for common UI action/state Iconify icon strings.
 *
 * Rule: any component using a generic UI icon (close, download, info, etc.)
 * MUST import from here. Never hardcode a UI icon string inline.
 *
 * To change an icon app-wide: edit one value here.
 *
 * Mirrors cloudfront_ui/src/lib/ui-icons.ts — keep in sync.
 */
export const UI_ICONS = {
  // --- Actions ---
  close:        'mdi:close',
  add:          'mdi:plus',
  edit:         'solar:pen-bold',
  delete:       'solar:trash-bin-trash-bold',
  save:         'mdi:content-save-outline',
  copy:         'mdi:content-copy',
  link:         'mdi:link',
  download:     'material-symbols:download-rounded',
  upload:       'mdi:upload',
  search:       'mdi:magnify',
  filter:       'mdi:filter-outline',
  refresh:      'mdi:refresh',
  send:         'mdi:send-outline',
  share:        'mdi:share-variant-outline',
  print:        'mdi:printer-outline',
  export:       'mdi:export-variant',

  // --- Visibility ---
  show:         'mdi:eye-outline',
  hide:         'mdi:eye-off-outline',
  fullscreen:   'mdi:fullscreen',
  fullscreenExit: 'mdi:fullscreen-exit',
  expand:       'mdi:chevron-down',
  collapse:     'mdi:chevron-up',

  // --- Navigation ---
  back:         'mdi:arrow-left',
  forward:      'mdi:arrow-right',
  up:           'mdi:arrow-up',
  down:         'mdi:arrow-down',
  externalLink: 'mdi:open-in-new',
  menu:         'mdi:menu',
  more:         'mdi:dots-vertical',
  moreHoriz:    'mdi:dots-horizontal',

  // --- Status / feedback ---
  info:         'mdi:information-outline',
  help:         'mdi:help-circle-outline',
  warning:      'mdi:alert-outline',
  error:        'mdi:alert-circle-outline',
  success:      'mdi:check-circle-outline',
  pending:      'mdi:clock-outline',
  loading:      'mdi:timer-sand',
  check:        'mdi:check',

  // --- Media controls ---
  play:         'mdi:play',
  pause:        'mdi:pause',
  stop:         'mdi:stop',

  // --- Data / content ---
  chart:        'mdi:chart-bar',
  chartLine:    'mdi:chart-line',
  trendUp:      'mdi:trending-up',
  trendDown:    'mdi:trending-down',
  table:        'mdi:table',
  database:     'mdi:database-outline',
  file:         'mdi:file-outline',
  folder:       'mdi:folder-outline',
  image:        'mdi:image-outline',
  document:     'solar:document-bold-duotone',

  // --- People / identity ---
  user:         'mdi:account-outline',
  users:        'mdi:account-group-outline',
  usersGroup:   'solar:users-group-rounded-bold',
  userAdd:      'mdi:account-plus-outline',
  userRemove:   'mdi:account-minus-outline',

  // --- Misc UI ---
  settings:     'mdi:cog-outline',
  notification: 'mdi:bell-outline',
  calendar:     'mdi:calendar-outline',
  location:     'mdi:map-marker-outline',
  tag:          'mdi:tag-outline',
  store:        'mdi:storefront-outline',
  cart:         'mdi:cart-outline',
  monitor:      'mdi:monitor',

  // --- AI / ML ---
  ai:           'humbleicons:ai',
  sparkles:     'mdi:sparkles',

  archive:      'solar:inbox-archive-bold',
  payment:      'solar:card-bold',
  terminate:    'solar:stop-circle-bold',
  activate:     'solar:play-bold',
  duplicate:    'solar:copy-bold',
  view:         'solar:eye-bold',
  sync:         'solar:refresh-circle-bold',
  narrative:    'solar:document-linear',
  enrich:       'solar:database-linear',

  // --- File states ---
  fileCheck:    'mdi:file-check',
  cloudUpload:  'mdi:cloud-upload-outline',

  // --- Connectivity ---
  connection:   'mdi:connection',
  powerPlug:    'mdi:power-plug',

  // --- Favourites ---
  star:         'mdi:star-outline',
  starFilled:   'mdi:star',

  // --- Creative / content ---
  creatives:    'solar:gallery-add-bold',

  // --- Lists / targeting ---
  list:         'mdi:format-list-bulleted',
  target:       'mdi:bullseye-arrow',

  // --- KPI card metrics ---
  kpiTotalCampaigns:   'solar:target-bold',
  kpiActiveCampaigns:  'solar:play-circle-bold',
  kpiPausedCampaigns:  'solar:pause-circle-bold',
  kpiDraftCampaigns:   'solar:document-bold',
  kpiTotalSent:        'solar:plain-2-bold',
  kpiTotalMailed:      'solar:letter-bold',
  kpiDeliveryRate:     'solar:check-circle-bold',
  kpiScanned:          'solar:eye-bold',
  kpiReadRate:         'solar:eye-bold',
  kpiOpened:           'solar:eye-bold',
  kpiClicked:          'solar:cursor-bold',
  kpiReplied:          'solar:reply-bold',
  kpiBounced:          'solar:letter-unread-bold',
  kpiTotalAudiences:   'solar:users-group-two-rounded-bold',
  kpiActiveAudiences:  'solar:user-bold-duotone',
  kpiReach:            'solar:target-outline',
  kpiGrowthRate:       'solar:chart-arrow-up-bold',
  kpiTotalPredictions: 'solar:chart-2-bold',
  kpiActiveModels:     'solar:database-bold',
  kpiAccuracy:         'solar:target-bold',
  kpiBusinessLift:     'solar:graph-up-bold',
  kpiTotalScans:       'solar:qr-code-bold',
  kpiUniqueScans:      'solar:user-check-bold',
  kpiConversions:      'solar:chart-square-bold',
  kpiEngagements:      'solar:hand-shake-bold',
  kpiRevenue:          'solar:dollar-minimalistic-bold',
  kpiAvgDuration:      'solar:clock-circle-bold',
  kpiNodes:            'solar:layers-bold',
  kpiEnrolled:         'solar:user-plus-bold',
  kpiTotalFiles:       'solar:document-bold',
  kpiActiveFiles:      'solar:play-circle-bold',
  kpiExecutions:       'solar:bolt-bold',
  kpiSuccessRate:      'solar:check-circle-bold',
  kpiTotalCreatives:   'solar:gallery-bold',
  kpiOrders:           'solar:cart-large-bold',
  kpiSpend:            'solar:dollar-minimalistic-bold',
  kpiImpressions:      'solar:eye-bold',
  kpiActions:          'solar:play-bold',
  kpiCampaignGoal:     'solar:flag-bold',

  // --- Solar Linear set for Blyn Agent ---
  closeLine:           'mingcute:close-line',
  addCircle:           'solar:add-circle-linear',
  arrowDown:           'solar:alt-arrow-down-linear',
  arrowLeft:           'solar:alt-arrow-left-linear',
  arrowRight:          'solar:alt-arrow-right-linear',
  arrowRightLinear:    'solar:arrow-right-linear',
  bellLinear:          'solar:bell-linear',
  boltLinear:          'solar:bolt-linear',
  bookLinear:          'solar:book-2-linear',
  brainLinear:         'solar:brain-linear',
  chatDots:            'solar:chat-dots-linear',
  chatLine:            'solar:chat-line-linear',
  checkCircleLinear:   'solar:check-circle-linear',
  checkRead:           'solar:check-read-linear',
  checkSquare:         'solar:check-square-linear',
  clapperboard:        'solar:clapperboard-linear',
  clockCircle:         'solar:clock-circle-linear',
  closeCircle:         'solar:close-circle-linear',
  dangerTriangle:      'solar:danger-triangle-linear',
  documentText:        'solar:document-text-linear',
  downloadLinear:      'solar:download-linear',
  folderLinear:        'solar:folder-linear',
  folderWithFiles:     'solar:folder-with-files-linear',
  history:             'solar:history-linear',
  layersLinear:        'solar:layers-linear',
  pauseLinear:         'solar:pause-linear',
  pencil2:             'solar:pen-2-linear',
  pencilLinear:        'solar:pen-linear',
  pinLinear:           'solar:pin-linear',
  questionCircle:      'solar:question-circle-linear',
  restart:             'solar:restart-linear',
  rocket:              'solar:rocket-linear',
  routeLinear:         'solar:route-linear',
  settingsSolar:       'solar:settings-linear',
  shareSolar:          'solar:share-linear',
  uploadSolar:         'solar:upload-linear',
  usersGroupTwoLinear: 'solar:users-group-two-rounded-linear',

  // --- Voice / Blyn Agent Shared Icons ---
  pen2:                 'solar:pen-2-bold',
  searchLinear:         'solar:magnifer-linear',
  uploadMinimalistic:   'solar:upload-minimalistic-bold',
  folderError:          'solar:folder-error-bold',
  phoneCallingDuotone:  'solar:phone-calling-bold-duotone',
  phoneBold:            'solar:phone-bold',
  transferHorizontal:   'solar:round-transfer-horizontal-bold',
  altArrowRightLinear:  'solar:alt-arrow-right-linear',
  microphoneLinear:     'solar:microphone-linear',

  // --- Workflow Templates / Node categories ---
  workflowQualifier:    'solar:routing-bold',
  workflowCallback:     'solar:phone-calling-bold',
  workflowCompliance:   'solar:chat-round-dots-bold',
  workflowSurvey:       'solar:clipboard-list-bold',
  workflowUserSpeak:    'solar:user-speak-bold',
  workflowBolt:         'solar:bolt-bold',
  workflowAlarm:        'solar:alarm-bold',
  workflowSnowflake:    'solar:snowflake-bold',

  // --- Solar Linear set for Blyn Agent (aliased) ---
  addCircleLinear:     'solar:add-circle-linear',
  arrowDownLinear:     'solar:alt-arrow-down-linear',
  arrowLeftLinear:     'solar:alt-arrow-left-linear',
  arrowRightLinear2:   'solar:arrow-right-linear',
  chatDotsLinear:      'solar:chat-dots-linear',
  chatLineLinear:      'solar:chat-line-linear',
  clapperboardLinear:  'solar:clapperboard-linear',
  clockCircleLinear:   'solar:clock-circle-linear',
  closeCircleLinear:   'solar:close-circle-linear',
  dangerTriangleLinear:'solar:danger-triangle-linear',
  documentTextLinear:  'solar:document-text-linear',
  folderWithFilesLinear:'solar:folder-with-files-linear',
  pencil2Linear:       'solar:pen-2-linear',
  settingsSolarLinear: 'solar:settings-linear',
  shareSolarLinear:    'solar:share-linear',
  uploadSolarLinear:   'solar:upload-linear',
} as const;

export type UiIconKey = keyof typeof UI_ICONS;

/**
 * Icons for grid/table row actions.
 */
export const ACTION_ICONS = {
  view:      UI_ICONS.view,
  edit:      UI_ICONS.edit,
  duplicate: UI_ICONS.duplicate,
  delete:    UI_ICONS.delete,
  activate:  UI_ICONS.activate,
  terminate: UI_ICONS.terminate,
  add:       UI_ICONS.add,
  export:    UI_ICONS.download,
  more:      UI_ICONS.moreHoriz,
  sync:      UI_ICONS.sync,
  archive:   UI_ICONS.archive,
  payment:   UI_ICONS.payment,
  help:      UI_ICONS.help,
  narrative: UI_ICONS.narrative,
  enrich:    UI_ICONS.enrich,
} as const;
